import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Fade,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchBlogs, fetchFeaturedBlogsThunk } from '../store/thunks/blogsThunks';
import { setSelectedCategory, setSearchQuery } from '@e-learning/shared/src/store/slices/blogsSlice';
import BlogCard from '../components/blogs/BlogCard';
import SEO from '../components/SEO';

const BlogListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { blogs, featuredBlogs, loading, error, selectedCategory, searchQuery } = useAppSelector(
    (state) => state.blogs
  );
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  useEffect(() => {
    dispatch(fetchBlogs());
    dispatch(fetchFeaturedBlogsThunk(3));
  }, [dispatch]);

  // Extract unique categories from blogs
  const categories = Array.from(new Set(blogs.map((blog) => blog.category))).filter(Boolean);

  // Filter blogs based on category and search
  const filteredBlogs = blogs.filter((blog) => {
    const matchesCategory = !selectedCategory || blog.category === selectedCategory;
    const matchesSearch =
      !localSearchQuery ||
      blog.title.toLowerCase().includes(localSearchQuery.toLowerCase()) ||
      blog.excerpt.toLowerCase().includes(localSearchQuery.toLowerCase()) ||
      blog.tags.some((tag) => tag.toLowerCase().includes(localSearchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalSearchQuery(value);
    dispatch(setSearchQuery(value));
  };

  const handleCategoryClick = (category: string | null) => {
    dispatch(setSelectedCategory(category));
  };

  const siteUrl = 'https://learn2skill.com';
  const blogListStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Learn2Skill Blog',
    description: 'Explore programming tutorials, coding tips, industry insights, and expert advice to enhance your learning journey',
    url: `${siteUrl}/blog`,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: blogs.length,
      itemListElement: blogs.slice(0, 10).map((blog, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'BlogPosting',
          headline: blog.title,
          url: `${siteUrl}/blog/${blog.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <SEO
        title="Programming Blog - Tutorials, Tips & Industry Insights | Learn2Skill"
        description="Explore our comprehensive programming blog featuring tutorials, coding tips, industry insights, best practices, and expert advice. Learn JavaScript, Python, React, and more with our expert-led content."
        keywords="programming blog, coding tutorials, programming tips, software development blog, web development tutorials, coding best practices, programming guides, tech blog, developer resources"
        url="/blog"
        type="website"
        structuredData={blogListStructuredData}
      />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header Section */}
        <Fade in timeout={600}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ flex: 1 }} />
            <Typography
              variant="h2"
              component="h1"
              sx={{
                flex: 1,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Blog
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              {isAuthenticated && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/blog/create')}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    },
                  }}
                >
                  Create Post
                </Button>
              )}
            </Box>
          </Box>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
            Discover tutorials, tips, industry news, and educational content to enhance your learning journey
          </Typography>
        </Box>
      </Fade>

      {/* Search and Filter Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            placeholder="Search blog posts..."
            value={localSearchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                background: 'rgba(30, 41, 59, 0.5)',
                '&:hover': {
                  background: 'rgba(30, 41, 59, 0.7)',
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Chip
              label="All"
              onClick={() => handleCategoryClick(null)}
              color={!selectedCategory ? 'primary' : 'default'}
              sx={{
                cursor: 'pointer',
                ...(!selectedCategory && {
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                }),
              }}
            />
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => handleCategoryClick(category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                sx={{
                  cursor: 'pointer',
                  ...(selectedCategory === category && {
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                  }),
                }}
              />
            ))}
          </Box>
        </Box>
      </Fade>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Blog Posts Grid */}
      {!loading && filteredBlogs.length > 0 && (
        <Grid container spacing={4}>
          {filteredBlogs.map((blog, index) => (
            <Grid item xs={12} sm={6} md={4} key={blog.id}>
              <BlogCard blog={blog} index={index} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {!loading && filteredBlogs.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
            No blog posts found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {searchQuery || selectedCategory
              ? 'Try adjusting your search or filter criteria'
              : 'Check back soon for new content!'}
          </Typography>
        </Box>
      )}
    </Container>
    </>
  );
};

export default BlogListPage;

